'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import type { CreationWithTask, TaskInfo } from '@/app/types';
import { TaskStatusAnimation } from '../TaskCard/components/TaskStatusAnimation';
import { useTaskState } from '../TaskCard/hooks/use-task-state';
import type { DisplayTaskState } from '../TaskCard/types';
import { getCompactTaskHistoryStateMessage } from './compact-task-history-item';

export const CompactTaskHistoryItem: FC<{
  task: TaskInfo;
  onOpenDetail: (creation: CreationWithTask) => void;
  onCompactCreationSelect?: (creation: CreationWithTask) => void;
  onCompactTaskSelect?: (task: TaskInfo, creation?: CreationWithTask, taskState?: DisplayTaskState) => void;
  failedStateTitle?: string;
}> = ({ task, onOpenDetail, onCompactCreationSelect, onCompactTaskSelect, failedStateTitle }) => {
  const t = useTranslations();
  const { taskState } = useTaskState(task);
  const creation = task.creations?.[0];
  const compactCover = creation?.cover_uri || creation?.nomark_uri || creation?.uri;
  const stateMessage = getCompactTaskHistoryStateMessage({ creation, task, taskState });
  const stateTitle = stateMessage
    ? stateMessage.titleKey === 'task.status.otherFailed.title' && failedStateTitle
      ? failedStateTitle
      : t(stateMessage.titleKey)
    : '';
  const isSelectable = Boolean(creation || stateMessage);

  const content = (
    <button
      type="button"
      disabled={!isSelectable}
      onClick={() => {
        if (stateMessage) {
          const creationWithTask = creation ? { ...creation, task } : undefined;
          if (onCompactTaskSelect) {
            onCompactTaskSelect(task, creationWithTask, taskState);
            return;
          }
          if (creationWithTask && onCompactCreationSelect) {
            onCompactCreationSelect(creationWithTask);
          }
          return;
        }
        if (!creation) return;
        const creationWithTask = { ...creation, task };
        if (onCompactCreationSelect) {
          onCompactCreationSelect(creationWithTask);
          return;
        }
        onOpenDetail(creationWithTask);
      }}
      className="group relative mx-auto block aspect-square w-full overflow-hidden rounded-12 bg-system-white04 text-left transition-colors hover:bg-system-white08 disabled:cursor-default sm:size-[112px]"
    >
      {stateMessage ? (
        <span className="relative flex size-full items-center justify-center text-system-white32">
          {stateMessage.loading === 'queueing' ? <TaskStatusAnimation /> : null}
          {stateMessage.loading === 'processing' ? <Loading className="relative z-10 size-5 animate-spin" /> : null}
          <span className="relative z-10 line-clamp-2 px-1.5 text-center text-[10px] text-system-white64 leading-[1.25]">
            {stateTitle}
          </span>
        </span>
      ) : compactCover ? (
        <img
          src={compactCover}
          alt=""
          className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        <span className="flex size-full items-center justify-center text-system-white32">
          <span className="size-2 rounded-full bg-system-white24" />
        </span>
      )}
    </button>
  );

  if (!stateMessage) {
    return content;
  }

  return (
    <CustomTooltip content={stateTitle} trigger="hover">
      {content}
    </CustomTooltip>
  );
};
